// v2 heron — a wading grey heron with every field mark: thin stilt legs with
// ankle knuckles and splayed toes, a slate body with folded wing slabs and a
// drooping tail, a shaggy chest plume hanging at the breast, a true two-bend
// S-neck, a white head wearing the black eyebrow crest that trails into
// occipital plumes, amber eyes and the long dagger bill. Two morphs: v0 the grey
// heron, v1 a great white egret (all-white, black legs, no black crest). Ported
// 1:1 from age-of-mcp's v2/heron.ts, using the library's shared `wetland`
// (eyes, leg) helpers.
import * as THREE from "three";
import { defineModel, wetland, type Part } from "../../lib";

const MORPHS = [
  { body: "#7e95a8", wing: "#66798a", breast: "#e6ecf0", neck: "#d8e2e8",
    head: "#eef2f4", legc: "#b89a52", bill: "#e8b83a", crest: "#20262e",
    crested: true },
  { body: "#eff3f4", wing: "#dfe7ea", breast: "#f6f8f9", neck: "#eef3f4",
    head: "#f7f9fa", legc: "#2a2e30", bill: "#e8a020", crest: "#dfe7ea",
    crested: false },
];

function heronParts(v: number): Part[] {
  const c = MORPHS[v % MORPHS.length]!;
  const parts: Part[] = [];
  // stilt legs with ankle knuckles and splayed toes
  for (const s of [1, -1]) {
    parts.push(...wetland.leg(0.004, s * 0.019, 0.175, 0.0035, c.legc));
    parts.push(
      { geometry: new THREE.IcosahedronGeometry(0.0045, 0), color: c.legc,
        position: [0.004, 0.075, s * 0.019] },
      { geometry: new THREE.BoxGeometry(0.018, 0.004, 0.004), color: c.legc,
        position: [0.012, 0.002, s * 0.019], rotation: [0, s * 0.25, 0] },
    );
  }
  parts.push(
    // slate body with folded wing slabs
    { geometry: new THREE.IcosahedronGeometry(0.042, 0), color: c.body,
      position: [-0.005, 0.175, 0], scale: [1.5, 0.9, 0.8] },
    { geometry: new THREE.IcosahedronGeometry(0.03, 0), color: c.wing,
      position: [-0.02, 0.185, 0.022], scale: [1.4, 0.75, 0.45] },
    { geometry: new THREE.IcosahedronGeometry(0.03, 0), color: c.wing,
      position: [-0.02, 0.185, -0.022], scale: [1.4, 0.75, 0.45] },
    // white breast with the shaggy plume hanging below
    { geometry: new THREE.IcosahedronGeometry(0.024, 0), color: c.breast,
      position: [0.032, 0.172, 0], scale: [0.9, 1.1, 0.75] },
    { geometry: new THREE.ConeGeometry(0.008, 0.03, 4), color: c.breast,
      position: [0.04, 0.148, 0], rotation: [0, 0, Math.PI + 0.15],
      scale: [0.6, 1, 0.6] },
    // drooping tail
    { geometry: new THREE.ConeGeometry(0.014, 0.04, 4), color: c.wing,
      position: [-0.062, 0.18, 0], rotation: [0, 0, Math.PI / 2 + 0.25],
      scale: [1, 1, 0.5] },
    // two-bend S-neck
    { geometry: new THREE.CylinderGeometry(0.0085, 0.011, 0.07, 5), color: c.neck,
      position: [0.035, 0.225, 0], rotation: [0, 0, -0.45] },
    { geometry: new THREE.CylinderGeometry(0.007, 0.0085, 0.06, 5), color: c.neck,
      position: [0.063, 0.275, 0], rotation: [0, 0, 0.35] },
    // head with the dagger bill
    { geometry: new THREE.IcosahedronGeometry(0.015, 0), color: c.head,
      position: [0.057, 0.312, 0], scale: [1.35, 0.9, 0.85] },
    { geometry: new THREE.ConeGeometry(0.0055, 0.05, 4), color: c.bill,
      position: [0.085, 0.308, 0], rotation: [0, 0, -1.62],
      scale: [0.8, 1, 0.9] },
    ...wetland.eyes(0.064, 0.318, 0.0105, 0.003, "#d8b030"),
  );
  if (c.crested) {
    // black eyebrow crest trailing into two occipital plumes
    parts.push(
      { geometry: new THREE.IcosahedronGeometry(0.009, 0), color: c.crest,
        position: [0.048, 0.322, 0], scale: [1.6, 0.45, 1.0] },
      { geometry: new THREE.CylinderGeometry(0.0012, 0.0012, 0.035, 3),
        color: c.crest, position: [0.028, 0.324, 0.003], rotation: [0, 0, 1.75] },
      { geometry: new THREE.CylinderGeometry(0.0012, 0.0012, 0.035, 3),
        color: c.crest, position: [0.028, 0.324, -0.003], rotation: [0, 0, 1.75] },
    );
  } else {
    // the egret's wispy white back plumes
    parts.push({ geometry: new THREE.ConeGeometry(0.009, 0.045, 4), color: c.crest,
      position: [-0.07, 0.192, 0], rotation: [0, 0, Math.PI / 2 + 0.35],
      scale: [0.6, 1, 0.8] });
  }
  return parts;
}

export const heronV2 = defineModel({
  id: "heron-v2",
  batchId: "fable",
  name: "Heron (v2)",
  tags: ["creature","heron","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly heron.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.09,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => heronParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => heronParts(1) },
  ],
  animations: [],
});
